import { useSelector, useDispatch, shallowEqual } from 'react-redux';
import { useMemo, useContext, createContext } from 'react';
import { bindActionCreators } from 'redux';
import { getToken, handleCallback, login, logout, } from '../redux/actions/auth';
export const ChordAuthClientContext = createContext(null);
ChordAuthClientContext.displayName = 'ChordAuthClientContext';
/**
 * The `useAuth` hook returns information about the current authentication
 * session, and functions for logging in and logging out. Chord's preferred
 * authentication provider is [Magic](https://magic.link/) through our
 * [chord-magic package](https://github.com/chordcommerce/chord-ui/pkgs/npm/chord-magic).
 *
 * @example
 *
 * ```javascript
 * import { useAuth } from '@chordcommerce/react-autonomy'
 *
 * const AccountPage = () => {
 *   const { isLoggedIn } = useAuth()
 *   if (!isLoggedIn) return
 *
 *   // return account page
 * }
 * ```
 *
 * @category Hook
 */
export function useAuth() {
    const authState = useSelector((state) => state.auth, shallowEqual);
    const authClient = useContext(ChordAuthClientContext);
    const dispatch = useDispatch();
    const hooks = useMemo(() => ({
        serviceClient: authClient.getServiceClient(),
        ...bindActionCreators({
            getToken,
            handleCallback,
            login,
            logout,
        }, dispatch),
    }), [dispatch, authClient]);
    return { ...hooks, ...authState };
}
